import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getBalance } from '../services/api';
import toast from 'react-hot-toast';
import { createChart, ColorType, CandlestickSeries } from 'lightweight-charts';

const COIN_IDS = {
  'BTC/USDT': 'bitcoin',
  'ETH/USDT': 'ethereum',
  'SOL/USDT': 'solana',
  'XRP/USDT': 'ripple',
  'BNB/USDT': 'binancecoin'
};

const COIN_SYMBOLS = {
  'BTC/USDT': 'BTC',
  'ETH/USDT': 'ETH',
  'SOL/USDT': 'SOL',
  'XRP/USDT': 'XRP',
  'BNB/USDT': 'BNB'
};

const Trading = () => {
  const [balance, setBalance] = useState(0);
  const [selectedCoin, setSelectedCoin] = useState('BTC/USDT');
  const [tradeSide, setTradeSide] = useState('BUY');
  const [orderType, setOrderType] = useState('MARKET');
  const [tradeAmount, setTradeAmount] = useState(10);
  const [limitPrice, setLimitPrice] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [candleData, setCandleData] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [priceChange24h, setPriceChange24h] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [openOrders, setOpenOrders] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const priceInterval = useRef(null);
  const balanceFetched = useRef(false);

  const decimals = selectedCoin === 'XRP/USDT' ? 4 : 2;
  const selectedSymbol = COIN_SYMBOLS[selectedCoin];
  const isPriceUp = priceChange24h >= 0;

  const generateCandleData = useCallback((basePrice, count = 60) => {
    const data = [];
    let time = Math.floor(Date.now() / 1000) - count * 300;
    let price = basePrice;
    for (let i = 0; i < count; i++) {
      const open = price;
      const change = (Math.random() - 0.5) * price * 0.003;
      const close = price + change;
      const high = Math.max(open, close) + Math.random() * price * 0.002;
      const low = Math.min(open, close) - Math.random() * price * 0.002;
      data.push({ time, open, high, low, close });
      price = close;
      time += 300;
    }
    return data;
  }, []);

  const initChart = useCallback(() => {
    if (!chartContainerRef.current) return;
    if (chartRef.current) chartRef.current.remove();
    const chart = createChart(chartContainerRef.current, {
      layout: { background: { type: ColorType.Solid, color: '#0f172a' }, textColor: '#94a3b8' },
      grid: { vertLines: { color: '#1e293b' }, horzLines: { color: '#1e293b' } },
      width: chartContainerRef.current.clientWidth,
      height: 400,
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: '#334155' },
      rightPriceScale: { borderColor: '#334155' },
      crosshair: { mode: 0 },
    });
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e', downColor: '#ef4444', borderVisible: false,
      wickUpColor: '#22c55e', wickDownColor: '#ef4444',
    });
    candlestickSeries.setData(candleData);
    chart.timeScale().fitContent();
    chartRef.current = chart;
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [candleData]);

  const fetchCurrentPrice = useCallback(async () => {
    try {
      const coinId = COIN_IDS[selectedCoin];
      const response = await fetch(`https://api.coingecko.com/api/v3/coins/${coinId}?localization=false&tickers=false&market_data=true`);
      const data = await response.json();
      const price = data.market_data?.current_price?.usd || 0;
      const change = data.market_data?.price_change_percentage_24h || 0;
      if (price > 0) {
        setCurrentPrice(price);
        setPriceChange24h(change);
        setLastUpdate(new Date());
        setLimitPrice(prev => prev || price.toFixed(decimals));
      }
      return price;
    } catch (error) {
      console.error('Failed to fetch price:', error);
      return null;
    }
  }, [selectedCoin, decimals]);

  const fetchHistoricalData = useCallback(async () => {
    try {
      const coinId = COIN_IDS[selectedCoin];
      const response = await fetch(`https://api.coingecko.com/api/v3/coins/${coinId}/ohlc?vs_currency=usd&days=1`);
      const data = await response.json();
      if (data && data.length > 0) {
        const formattedData = data.map(item => ({ time: item[0] / 1000, open: item[1], high: item[2], low: item[3], close: item[4] }));
        setCandleData(formattedData);
        setCurrentPrice(formattedData[formattedData.length - 1]?.close || 0);
      } else {
        const basePrice = selectedCoin === 'BTC/USDT' ? 65000 : selectedCoin === 'ETH/USDT' ? 3200 : selectedCoin === 'SOL/USDT' ? 140 : selectedCoin === 'XRP/USDT' ? 0.52 : 580;
        setCandleData(generateCandleData(basePrice));
        setCurrentPrice(basePrice);
      }
    } catch (error) {
      const basePrice = selectedCoin === 'BTC/USDT' ? 65000 : 3200;
      setCandleData(generateCandleData(basePrice));
    }
  }, [selectedCoin, generateCandleData]);

  const startPriceUpdates = useCallback(() => {
    if (priceInterval.current) clearInterval(priceInterval.current);
    priceInterval.current = setInterval(async () => { await fetchCurrentPrice(); }, 10000);
  }, [fetchCurrentPrice]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchHistoricalData();
      await fetchCurrentPrice();
      startPriceUpdates();
      setLoading(false);
    };
    init();
    return () => { if (priceInterval.current) clearInterval(priceInterval.current); };
  }, [selectedCoin, fetchHistoricalData, fetchCurrentPrice, startPriceUpdates]);

  useEffect(() => {
    if (!loading && candleData.length > 0) {
      const cleanup = initChart();
      return cleanup;
    }
  }, [loading, candleData, selectedCoin, initChart]);

  useEffect(() => {
    if (!balanceFetched.current) {
      balanceFetched.current = true;
      const fetchBalanceOnce = async () => {
        try {
          const response = await getBalance();
          if (response.data.success) setBalance(response.data.balance || 0);
        } catch (error) { console.error('Failed to fetch balance'); }
      };
      fetchBalanceOnce();
    }
  }, []);

  const getFormattedPrice = (value = currentPrice) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const estimatedQty = useMemo(() => {
    const price = orderType === 'LIMIT' ? Number(limitPrice || 0) : currentPrice;
    if (!price || !tradeAmount) return 0;
    return tradeAmount / price;
  }, [tradeAmount, limitPrice, orderType, currentPrice]);

  const maxTotal = 150000;
  const mockOrderBook = useMemo(() => {
    if (!currentPrice) return { asks: [], bids: [] };
    const asks = Array.from({ length: 12 }, (_, i) => {
      const price = currentPrice + (i + 1) * currentPrice * 0.0006;
      const amount = (Math.random() * 1.5 + 0.05).toFixed(5);
      const total = (Number(amount) * price).toFixed(0);
      return { price, amount, total };
    }).reverse();
    const bids = Array.from({ length: 12 }, (_, i) => {
      const price = currentPrice - (i + 1) * currentPrice * 0.0006;
      const amount = (Math.random() * 1.5 + 0.05).toFixed(5);
      const total = (Number(amount) * price).toFixed(0);
      return { price, amount, total };
    });
    return { asks, bids };
  }, [currentPrice]);

  const mockTrades = useMemo(() => {
    return Array.from({ length: 15 }, (_, i) => {
      const direction = Math.random() > 0.5 ? 'buy' : 'sell';
      const variance = (Math.random() - 0.5) * currentPrice * 0.0015;
      return { price: Math.max(currentPrice + variance, 0), amount: (Math.random() * 0.6 + 0.01).toFixed(5), time: new Date(Date.now() - i * 12000).toLocaleTimeString(), direction };
    });
  }, [currentPrice]);

  const balancesData = useMemo(() => {
    const cryptoAmounts = { 'BTC/USDT': 0.02321, 'ETH/USDT': 0.84215, 'SOL/USDT': 12.482, 'XRP/USDT': 1850.45, 'BNB/USDT': 3.215 };
    const cryptoAmount = cryptoAmounts[selectedCoin] || 0;
    const balanceValue = (cryptoAmount * currentPrice).toFixed(2);
    return [
      { asset: 'USDT', balance: balance.toFixed(2), value: balance.toFixed(2) },
      { asset: selectedSymbol, balance: cryptoAmount.toFixed(selectedCoin === 'XRP/USDT' ? 2 : 5), value: balanceValue }
    ];
  }, [balance, selectedCoin, selectedSymbol, currentPrice]);

  const handleTrade = async () => {
    if (tradeAmount > balance) { toast.error('Insufficient balance'); return; }
    if (tradeAmount < 10) { toast.error('Minimum order amount is 10 USDT'); return; }
    setIsProcessing(true);
    setTimeout(() => {
      const orderPrice = orderType === 'LIMIT' ? Number(limitPrice || currentPrice) : currentPrice;
      const newOrder = { id: Date.now(), pair: selectedCoin, side: tradeSide, type: orderType, price: orderPrice, amount: tradeAmount, qty: estimatedQty, status: orderType === 'MARKET' ? 'Filled' : 'Open' };
      setOpenOrders(prev => [newOrder, ...prev].slice(0, 8));
      toast.success(`${tradeSide} ${orderType} order placed`);
      setIsProcessing(false);
    }, 1200);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[500px] rounded-2xl border border-slate-800 bg-slate-950 text-slate-400 m-4">Loading market data...</div>;
  }

  return (
    <div className="min-h-screen text-white p-4" style={{ background: 'linear-gradient(180deg, #0b1120 0%, #0f172a 100%)' }}>
      <div className="mx-auto overflow-hidden rounded-2xl border border-slate-800 shadow-2xl" style={{ background: '#020617' }}>
        {/* Header */}
        <div className="flex flex-col gap-3 border-b border-slate-800 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden" style={{ background: '#111827', border: '1px solid #1e293b', borderRadius: 10, color: '#cbd5e1', padding: '8px 12px' }}>☰</button>
            <div><div className="text-lg font-semibold">{selectedCoin}</div><div className="text-xs text-slate-400">Spot Market</div></div>
            <div className="hidden h-8 w-px bg-slate-800 lg:block" />
            <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Last Price</div><div className={`text-lg font-bold ${isPriceUp ? 'text-emerald-400' : 'text-red-400'}`}>${getFormattedPrice()}</div></div>
            <div><div className="text-[11px] uppercase tracking-wide text-slate-500">24H Change</div><div className={`${isPriceUp ? 'text-emerald-400' : 'text-red-400'} text-sm font-semibold`}>{isPriceUp ? '+' : '-'}{Math.abs(priceChange24h).toFixed(2)}%</div></div>
          </div>
          <div className="rounded-xl px-4 py-3" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
            <div className="text-[11px] uppercase tracking-wide text-emerald-300/80">Available Balance</div>
            <div className="text-xl font-bold text-emerald-400">{balance.toFixed(2)} USDT</div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="border-b border-slate-800 p-4 lg:hidden">
            <div className="flex flex-wrap gap-2">
              {Object.keys(COIN_IDS).map((coinValue) => (
                <button
                  key={coinValue}
                  onClick={() => { setSelectedCoin(coinValue); setIsMobileMenuOpen(false); }}
                  style={{
                    background: selectedCoin === coinValue ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#0b1220',
                    color: selectedCoin === coinValue ? '#ffffff' : '#93c5fd',
                    border: selectedCoin === coinValue ? '1px solid #60a5fa' : '1px solid #1e3a8a',
                    borderRadius: 10,
                    padding: '8px 12px',
                    fontSize: 12,
                    fontWeight: 700
                  }}
                >
                  {COIN_SYMBOLS[coinValue]}/USDT
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Main Grid */}
        <div className="grid grid-cols-1 gap-px bg-slate-800 xl:grid-cols-12">
          {/* Left Panel - Order Form */}
          <div className="xl:col-span-3 bg-slate-950 p-4">
            {/* Buy/Sell Buttons */}
            <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl p-1" style={{ background: '#0f172a' }}>
              <button
                onClick={() => setTradeSide('BUY')}
                style={{
                  background: tradeSide === 'BUY' ? 'linear-gradient(135deg, #22c55e, #10b981)' : '#0b1220',
                  color: tradeSide === 'BUY' ? '#ffffff' : '#93c5fd',
                  border: tradeSide === 'BUY' ? '1px solid #34d399' : '1px solid #1d4ed8',
                  borderRadius: 12,
                  padding: '12px 14px',
                  fontWeight: 800,
                  boxShadow: tradeSide === 'BUY' ? '0 0 0 1px rgba(52,211,153,0.15), 0 8px 20px rgba(16,185,129,0.25)' : 'none'
                }}
              >
                Buy
              </button>
              <button
                onClick={() => setTradeSide('SELL')}
                style={{
                  background: tradeSide === 'SELL' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : '#0b1220',
                  color: tradeSide === 'SELL' ? '#ffffff' : '#fca5a5',
                  border: tradeSide === 'SELL' ? '1px solid #f87171' : '1px solid #7f1d1d',
                  borderRadius: 12,
                  padding: '12px 14px',
                  fontWeight: 800,
                  boxShadow: tradeSide === 'SELL' ? '0 0 0 1px rgba(248,113,113,0.15), 0 8px 20px rgba(239,68,68,0.25)' : 'none'
                }}
              >
                Sell
              </button>
            </div>

            {/* Coin Selector Buttons (Desktop) */}
            <div className="mb-4 hidden flex-wrap gap-2 lg:flex">
              {Object.keys(COIN_IDS).map((coinValue) => (
                <button
                  key={coinValue}
                  onClick={() => setSelectedCoin(coinValue)}
                  style={{
                    background: selectedCoin === coinValue ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#0b1220',
                    color: selectedCoin === coinValue ? '#ffffff' : '#93c5fd',
                    border: selectedCoin === coinValue ? '1px solid #60a5fa' : '1px solid #1e3a8a',
                    borderRadius: 12,
                    padding: '9px 14px',
                    fontSize: 12,
                    fontWeight: 700,
                    boxShadow: selectedCoin === coinValue ? '0 8px 20px rgba(37,99,235,0.25)' : 'none'
                  }}
                >
                  {COIN_SYMBOLS[coinValue]}/USDT
                </button>
              ))}
            </div>

            {/* MARKET / LIMIT Buttons */}
            <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl p-1" style={{ background: '#0f172a' }}>
              {['MARKET', 'LIMIT'].map((type) => (
                <button
                  key={type}
                  onClick={() => setOrderType(type)}
                  style={{
                    background: orderType === type ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#0b1220',
                    color: orderType === type ? '#ffffff' : '#93c5fd',
                    border: orderType === type ? '1px solid #60a5fa' : '1px solid #1e3a8a',
                    borderRadius: 12,
                    padding: '12px 14px',
                    fontWeight: 700,
                    boxShadow: orderType === type ? '0 8px 20px rgba(37,99,235,0.22)' : 'none'
                  }}
                >
                  {type}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {orderType === 'LIMIT' && (
                <div><label className="mb-2 block text-xs uppercase tracking-wide text-slate-500">Limit Price</label><input type="number" value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)} placeholder="Enter price" style={{ width: '100%', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: '12px 14px', outline: 'none' }} /></div>
              )}
              <div>
                <label className="mb-2 block text-xs uppercase tracking-wide text-slate-500">Amount (USDT)</label>
                <input type="number" value={tradeAmount} onChange={(e) => setTradeAmount(Number(e.target.value))} min="10" style={{ width: '100%', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: '12px 14px', outline: 'none' }} />
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {[10, 50, 100, 500].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setTradeAmount(amt)}
                      style={{
                        background: tradeAmount === amt ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#0b1220',
                        color: tradeAmount === amt ? '#ffffff' : '#93c5fd',
                        border: tradeAmount === amt ? '1px solid #60a5fa' : '1px solid #1e3a8a',
                        borderRadius: 10,
                        padding: '8px 10px',
                        fontSize: 12,
                        fontWeight: 700
                      }}
                    >
                      {amt}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-xl p-4" style={{ background: '#0f172a', border: '1px solid #1e293b' }}>
                <div className="mb-2 flex justify-between"><span className="text-slate-400">Estimated Quantity</span><span className="font-medium text-white">{estimatedQty.toFixed(selectedCoin === 'XRP/USDT' ? 2 : 6)} {selectedSymbol}</span></div>
                <div className="mb-2 flex justify-between"><span className="text-slate-400">Available</span><span className="font-medium text-white">{balance.toFixed(2)} USDT</span></div>
                <div className="mb-2 flex justify-between"><span className="text-slate-400">Est. Order Value</span><span className="font-medium text-white">${tradeAmount.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Reference Price</span><span className="font-medium text-white">${getFormattedPrice(orderType === 'LIMIT' ? Number(limitPrice || 0) : currentPrice)}</span></div>
              </div>

              {/* Main Submit Button */}
              <button
                onClick={handleTrade}
                disabled={isProcessing || tradeAmount > balance}
                style={{
                  width: '100%',
                  background: tradeSide === 'BUY'
                    ? 'linear-gradient(135deg, #22c55e, #10b981)'
                    : 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: '#ffffff',
                  border: tradeSide === 'BUY' ? '1px solid #34d399' : '1px solid #f87171',
                  borderRadius: 14,
                  padding: '14px',
                  fontWeight: 800,
                  letterSpacing: '0.02em',
                  opacity: isProcessing || tradeAmount > balance ? 0.5 : 1,
                  cursor: isProcessing || tradeAmount > balance ? 'not-allowed' : 'pointer',
                  boxShadow: tradeSide === 'BUY'
                    ? '0 10px 25px rgba(16,185,129,0.30)'
                    : '0 10px 25px rgba(239,68,68,0.30)'
                }}
              >
                {isProcessing ? 'PROCESSING...' : `${tradeSide} ${selectedCoin}`}
              </button>
            </div>
          </div>

          {/* Middle Panel - Chart */}
          <div className="xl:col-span-6 bg-slate-950 p-4">
            <div className="mb-3"><div className="text-sm font-semibold text-white">{selectedCoin} Chart</div><div className="text-xs text-slate-500">{lastUpdate ? `Updated ${lastUpdate.toLocaleTimeString()}` : 'Live candlestick chart'}</div></div>
            <div ref={chartContainerRef} className="w-full rounded-2xl border border-slate-800" style={{ height: 420, background: '#0f172a', padding: 8 }} />
          </div>

          {/* Right Panel - Order Book & Recent Trades */}
          <div className="xl:col-span-3 bg-slate-950 p-4 space-y-4">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-3 overflow-hidden">
              <div className="mb-3 flex justify-between"><div><div className="text-sm font-semibold text-white">Order Book</div><div className="text-xs text-slate-500">{selectedCoin}</div></div><div className="text-[11px] text-slate-500">Live</div></div>
              <div className="grid grid-cols-3 items-center border-b border-slate-800 px-2 pb-2 text-[11px] uppercase tracking-wide text-slate-500"><div className="font-medium">Price</div><div className="text-right font-medium">Amount</div><div className="text-right font-medium">Total</div></div>
              <div className="max-h-[260px] overflow-y-auto pr-1">
                <div className="space-y-1 pt-2">
                  {mockOrderBook.asks.map((row, idx) => (
                    <button key={`ask-${idx}`} type="button" onClick={() => { setOrderType('LIMIT'); setLimitPrice(Number(row.price).toFixed(decimals)); }} className="relative grid w-full grid-cols-3 items-center rounded-md px-2 py-1.5 text-xs transition hover:bg-slate-800/60">
                      <div className="absolute inset-y-0 right-0 rounded-md bg-red-500/10" style={{ width: `${Math.min((Number(row.total) / maxTotal) * 100, 100)}%` }} />
                      <div className="relative z-10 truncate text-left font-medium text-red-400">${getFormattedPrice(row.price)}</div>
                      <div className="relative z-10 text-right text-slate-300">{row.amount}</div>
                      <div className="relative z-10 text-right text-slate-500">{Number(row.total).toLocaleString()}</div>
                    </button>
                  ))}
                </div>
                <div className="my-2 rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-center">
                  <div className={`text-base font-bold ${isPriceUp ? 'text-emerald-400' : 'text-red-400'}`}>${getFormattedPrice()}</div>
                  <div className="text-[11px] text-slate-500">Mark Price</div>
                </div>
                <div className="space-y-1 pb-2">
                  {mockOrderBook.bids.map((row, idx) => (
                    <button key={`bid-${idx}`} type="button" onClick={() => { setOrderType('LIMIT'); setLimitPrice(Number(row.price).toFixed(decimals)); }} className="relative grid w-full grid-cols-3 items-center rounded-md px-2 py-1.5 text-xs transition hover:bg-slate-800/60">
                      <div className="absolute inset-y-0 right-0 rounded-md bg-emerald-500/10" style={{ width: `${Math.min((Number(row.total) / maxTotal) * 100, 100)}%` }} />
                      <div className="relative z-10 truncate text-left font-medium text-emerald-400">${getFormattedPrice(row.price)}</div>
                      <div className="relative z-10 text-right text-slate-300">{row.amount}</div>
                      <div className="relative z-10 text-right text-slate-500">{Number(row.total).toLocaleString()}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-3">
              <div className="mb-3 text-sm font-semibold text-white">Recent Trades</div>
              <div className="mb-2 grid grid-cols-3 border-b border-slate-800 pb-2 text-[11px] uppercase tracking-wide text-slate-500"><div>Price</div><div className="text-right">Amount</div><div className="text-right">Time</div></div>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {mockTrades.map((trade, idx) => (
                  <div key={idx} className="grid grid-cols-3 rounded-md px-2 py-1 text-xs hover:bg-slate-800/40">
                    <div className={trade.direction === 'buy' ? 'text-emerald-400' : 'text-red-400'}>${getFormattedPrice(trade.price)}</div>
                    <div className="text-right text-slate-300">{trade.amount}</div>
                    <div className="text-right text-slate-500">{trade.time}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Balances & Open Orders */}
        <div className="grid grid-cols-1 gap-px border-t border-slate-800 bg-slate-800 lg:grid-cols-2">
          <div className="bg-slate-950 p-4 overflow-x-auto">
            <div className="mb-3 text-sm font-semibold text-white">Balances</div>
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-slate-500">
                  <th className="pb-3 font-medium">Asset</th>
                  <th className="pb-3 font-medium">Balance</th>
                  <th className="pb-3 font-medium">Value (USD)</th>
                </tr>
              </thead>
              <tbody>
                {balancesData.map((row) => (
                  <tr key={row.asset} className="border-b border-slate-900">
                    <td className="py-3 text-white">{row.asset}</td>
                    <td className="py-3 text-slate-300">{row.balance}</td>
                    <td className="py-3 text-slate-300">${Number(row.value).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-slate-950 p-4 overflow-x-auto">
            <div className="mb-3 text-sm font-semibold text-white">Open Orders</div>
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-slate-500">
                  <th className="pb-3 font-medium">Pair</th>
                  <th className="pb-3 font-medium">Side</th>
                  <th className="pb-3 font-medium">Type</th>
                  <th className="pb-3 font-medium">Price</th>
                  <th className="pb-3 font-medium">Amount</th>
                  <th className="pb-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {openOrders.length === 0 ? (
                  <tr><td colSpan="6" className="py-6 text-center text-slate-500">No orders yet</td></tr>
                ) : (
                  openOrders.map((order) => (
                    <tr key={order.id} className="border-b border-slate-900">
                      <td className="py-3 text-white">{order.pair}</td>
                      <td className={`py-3 ${order.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'}`}>{order.side}</td>
                      <td className="py-3 text-slate-300">{order.type}</td>
                      <td className="py-3 text-slate-300">${getFormattedPrice(order.price)}</td>
                      <td className="py-3 text-slate-300">${order.amount.toFixed(2)}</td>
                      <td className="py-3 text-slate-300">{order.status}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Trading;